import { useState } from "react";
import { Link } from "react-router-dom";
import { useTeamworkDuplicates } from "../hooks/useTeamworkDuplicates";
import type {
  Confidence,
  DuplicatePair,
  TimeEntry,
} from "../lib/detectDuplicates";
import styles from "./TeamworkDuplicates.module.css";

function formatDuration(entry: TimeEntry): string {
  const h = parseInt(entry.hours, 10);
  const m = parseInt(entry.minutes, 10);
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("en-AU", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function personName(entry: TimeEntry): string {
  return `${entry["person-first-name"]} ${entry["person-last-name"]}`;
}

export default function TeamworkDuplicates() {
  const { pairs, loading, error, deleting, deleteEntry, refresh } =
    useTeamworkDuplicates();
  const [filter, setFilter] = useState<Confidence | null>(null);
  const [swapped, setSwapped] = useState<Set<string>>(new Set());

  const filtered = filter
    ? pairs.filter((p) => p.confidence === filter)
    : pairs;
  const highCount = pairs.filter((p) => p.confidence === "HIGH").length;
  const medCount = pairs.filter((p) => p.confidence === "MEDIUM").length;
  const lowCount = pairs.filter((p) => p.confidence === "LOW").length;

  function chipClass(level: Confidence | null): string {
    const base = styles.summaryChip;
    const active = filter === level ? ` ${styles.summaryChipActive}` : "";
    if (level === "HIGH") return `${base} ${styles.summaryChipHigh}${active}`;
    if (level === "MEDIUM")
      return `${base} ${styles.summaryChipMedium}${active}`;
    if (level === "LOW") return `${base} ${styles.summaryChipLow}${active}`;
    return `${base}${active}`;
  }

  function confClass(c: Confidence): string {
    if (c === "HIGH")
      return `${styles.confidenceBadge} ${styles.confidenceHigh}`;
    if (c === "MEDIUM")
      return `${styles.confidenceBadge} ${styles.confidenceMedium}`;
    return `${styles.confidenceBadge} ${styles.confidenceLow}`;
  }

  function toggleSwap(pairKey: string) {
    setSwapped((prev) => {
      const next = new Set(prev);
      if (next.has(pairKey)) next.delete(pairKey);
      else next.add(pairKey);
      return next;
    });
  }

  function renderMetaRow(
    label: string,
    keepVal: string,
    suspectVal: string,
    side: "keep" | "suspect",
  ) {
    const value = side === "keep" ? keepVal : suspectVal;
    const differs = keepVal !== suspectVal;
    const rowClass = differs
      ? `${styles.entryMetaRow} ${styles.diffHighlight}`
      : styles.entryMetaRow;
    return (
      <div key={label} className={rowClass}>
        <span className={styles.entryMetaLabel}>{label}</span>
        <span className={styles.entryMetaValue}>{value || "\u2014"}</span>
      </div>
    );
  }

  function renderPair(pair: DuplicatePair) {
    const pairKey = `${pair.a.id}-${pair.b.id}`;
    const isSwapped = swapped.has(pairKey);
    const keep = isSwapped ? pair.b : pair.a;
    const suspect = isSwapped ? pair.a : pair.b;
    const isSuspectDeleting = deleting.has(suspect.id);

    const sameProject =
      keep["project-id"] === suspect["project-id"] && keep["project-name"];

    return (
      <div key={pairKey} className={styles.pairCard}>
        <div className={styles.pairHeader}>
          <span className={confClass(pair.confidence)}>{pair.confidence}</span>
          <span className={styles.pairScore}>Score: {pair.score}/100</span>
          <div className={styles.sharedContext}>
            <span className={styles.sharedContextItem}>
              <span className={styles.sharedContextLabel}>Person:</span>
              {personName(keep)}
            </span>
            {sameProject && (
              <span className={styles.sharedContextItem}>
                <span className={styles.sharedContextLabel}>Project:</span>
                {keep["project-name"]}
              </span>
            )}
          </div>
        </div>

        <div className={styles.pairReasons}>
          {pair.reasons.map((r) => (
            <span key={r} className={styles.reasonTag}>
              {r}
            </span>
          ))}
        </div>

        <div className={styles.pairEntries}>
          {/* Keep side */}
          <div className={styles.entryKeep}>
            <div className={styles.entryLabelRow}>
              <span className={styles.keepLabel}>KEEP</span>
              <button
                className={styles.swapBtn}
                onClick={() => toggleSwap(pairKey)}
                title="Swap keep/delete sides"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M11 1l3 3-3 3M1 4h13M5 15l-3-3 3-3M15 12H2" />
                </svg>
              </button>
            </div>
            <div className={styles.entryDesc}>
              {keep.description || "(no description)"}
            </div>
            <div className={styles.entryMeta}>
              {!sameProject &&
                renderMetaRow(
                  "Project",
                  keep["project-name"],
                  suspect["project-name"],
                  "keep",
                )}
              {renderMetaRow(
                "Task",
                keep["todo-item-name"],
                suspect["todo-item-name"],
                "keep",
              )}
              {renderMetaRow(
                "Start",
                formatTime(keep.date),
                formatTime(suspect.date),
                "keep",
              )}
              {renderMetaRow(
                "Duration",
                formatDuration(keep),
                formatDuration(suspect),
                "keep",
              )}
              {renderMetaRow(
                "Billable",
                keep.isbillable === "1" ? "Yes" : "No",
                suspect.isbillable === "1" ? "Yes" : "No",
                "keep",
              )}
            </div>
          </div>

          {/* Suspect side */}
          <div className={styles.entrySuspect}>
            <div className={styles.entryLabelRow}>
              <span className={styles.suspectLabel}>LIKELY DUPLICATE</span>
            </div>
            <div className={styles.entryDesc}>
              {suspect.description || "(no description)"}
            </div>
            <div className={styles.entryMeta}>
              {!sameProject &&
                renderMetaRow(
                  "Project",
                  keep["project-name"],
                  suspect["project-name"],
                  "suspect",
                )}
              {renderMetaRow(
                "Task",
                keep["todo-item-name"],
                suspect["todo-item-name"],
                "suspect",
              )}
              {renderMetaRow(
                "Start",
                formatTime(keep.date),
                formatTime(suspect.date),
                "suspect",
              )}
              {renderMetaRow(
                "Duration",
                formatDuration(keep),
                formatDuration(suspect),
                "suspect",
              )}
              {renderMetaRow(
                "Billable",
                keep.isbillable === "1" ? "Yes" : "No",
                suspect.isbillable === "1" ? "Yes" : "No",
                "suspect",
              )}
            </div>
            <button
              className={styles.deleteBtnFull}
              disabled={isSuspectDeleting}
              onClick={() => void deleteEntry(suspect.id)}
            >
              {isSuspectDeleting ? "Deleting\u2026" : "Delete this duplicate"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <Link to="/" className={styles.backLink}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
          Home
        </Link>
        <div className={styles.headerTitle}>
          <span className={styles.toolName}>Duplicate Time Logs</span>
          <span className={styles.badge}>App</span>
          <button
            className={styles.refreshBtn}
            onClick={refresh}
            disabled={loading}
          >
            Refresh
          </button>
        </div>
      </div>

      {error && <div className={styles.errorMsg}>{error}</div>}

      {loading && (
        <div className={styles.emptyState}>
          <div className={styles.spinner} />
          Scanning time entries from the last 7 days...
        </div>
      )}

      {!loading && pairs.length === 0 && !error && (
        <div className={styles.emptyState}>
          No suspected duplicates found in the last 7 days.
        </div>
      )}

      {!loading && pairs.length > 0 && (
        <>
          <div className={styles.summary}>
            <button className={chipClass(null)} onClick={() => setFilter(null)}>
              All {pairs.length}
            </button>
            {highCount > 0 && (
              <button
                className={chipClass("HIGH")}
                onClick={() => setFilter(filter === "HIGH" ? null : "HIGH")}
              >
                High {highCount}
              </button>
            )}
            {medCount > 0 && (
              <button
                className={chipClass("MEDIUM")}
                onClick={() => setFilter(filter === "MEDIUM" ? null : "MEDIUM")}
              >
                Medium {medCount}
              </button>
            )}
            {lowCount > 0 && (
              <button
                className={chipClass("LOW")}
                onClick={() => setFilter(filter === "LOW" ? null : "LOW")}
              >
                Low {lowCount}
              </button>
            )}
          </div>

          <div className={styles.pairList}>
            {filtered.map((pair) => renderPair(pair))}
          </div>
        </>
      )}
    </div>
  );
}
